import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Dropdown, Button, Icon } from 'semantic-ui-react';

const DropdownComponent = ({ dcSweepValue, setDcSweepValue, analysisMode, setAnalysisMode, dcSweepOptions, setNodes, isMobile, displayMode, themeColors }) => {

    // Generate stable unique ID for this component instance (React 16/17 compatible)
    const idRef = useRef(null);
    if (idRef.current === null) {
        idRef.current = 'dd' + Math.random().toString(36).substr(2, 9);
    }
    const uniqueId = idRef.current;

    const options = [
        { key: 'time-domain', text: l.time_domain, value: 'time_domain' },
        { key: 'ac-sweep', text: l.ac_sweep || 'AC Sweep', value: 'ac_sweep' },
        { key: 'dc-sweep', text: l.dc_sweep, value: 'dc_sweep' }
    ];

    const handleChange = (e, { value }) => {
        if (value != 'dc_sweep')
            setDcSweepValue('');

        setAnalysisMode(value);
    };

    const handleDcSweepChange = (e, { value }) => {
        com.clientside.main.Html5Bootstrap.jsCall("setDcSweepSource", value);
        setDcSweepValue(value);
    };

    const isSmall = isMobile || displayMode === 'small';

    // Create dropdown styles based on theme colors
    const dropdownStyle = themeColors ? {
        backgroundColor: themeColors.dropdownBg,
        color: themeColors.dropdownText,
        borderColor: themeColors.dropdownBorder
    } : {};

    // Inject dynamic styles for hover states
    useEffect(() => {
        if (!themeColors) return;

        const styleId = `chart-dropdown-styles-${uniqueId}`;
        let styleEl = document.getElementById(styleId);

        if (!styleEl) {
            styleEl = document.createElement('style');
            styleEl.id = styleId;
            document.head.appendChild(styleEl);
        }

        styleEl.textContent = `
            .chart-dropdown-${uniqueId}.ui.selection.dropdown {
                background-color: ${themeColors.dropdownBg} !important;
                border-color: ${themeColors.dropdownBorder} !important;
                color: ${themeColors.dropdownText} !important;
            }
            .ui.dropdown.chart-dropdown-${uniqueId} .menu,
            .ui.selection.dropdown.chart-dropdown-${uniqueId} .menu,
            .chart-dropdown-${uniqueId} .menu,
            .chart-dropdown-${uniqueId} .visible.menu,
            .chart-dropdown-${uniqueId}.visible .menu {
                background-color: ${themeColors.dropdownBg} !important;
                border-color: ${themeColors.dropdownBorder} !important;
            }
            .ui.dropdown.chart-dropdown-${uniqueId} .menu > .item,
            .ui.selection.dropdown.chart-dropdown-${uniqueId} .menu > .item,
            .chart-dropdown-${uniqueId} .menu > .item,
            .chart-dropdown-${uniqueId} .visible.menu > .item {
                background-color: ${themeColors.dropdownBg} !important;
                color: ${themeColors.dropdownText} !important;
                border-top: 1px solid ${themeColors.dropdownBorder} !important;
            }
            .ui.dropdown.chart-dropdown-${uniqueId} .menu > .item:hover,
            .ui.selection.dropdown.chart-dropdown-${uniqueId} .menu > .item:hover,
            .ui.dropdown.chart-dropdown-${uniqueId} .menu .item:hover,
            .chart-dropdown-${uniqueId} .menu > .item:hover,
            .chart-dropdown-${uniqueId} .visible.menu > .item:hover,
            .chart-dropdown-${uniqueId}.ui.dropdown .menu > .item:hover {
                background-color: ${themeColors.dropdownBg} !important;
                color: ${themeColors.dropdownText} !important;
            }
            .ui.dropdown.chart-dropdown-${uniqueId} .menu > .item.selected,
            .ui.dropdown.chart-dropdown-${uniqueId} .menu > .item.active,
            .ui.selection.dropdown.chart-dropdown-${uniqueId} .menu > .item.selected,
            .ui.selection.dropdown.chart-dropdown-${uniqueId} .menu > .item.active,
            .chart-dropdown-${uniqueId} .menu > .item.selected,
            .chart-dropdown-${uniqueId} .menu > .item.active,
            .chart-dropdown-${uniqueId} .visible.menu > .item.selected,
            .chart-dropdown-${uniqueId} .visible.menu > .item.active {
                background-color: ${themeColors.dropdownSelected} !important;
                color: ${themeColors.dropdownSelectedText} !important;
                font-weight: bold;
            }
            .chart-dropdown-${uniqueId} .text,
            .chart-dropdown-${uniqueId} > .text {
                color: ${themeColors.dropdownText} !important;
            }
            .chart-dropdown-${uniqueId} .dropdown.icon,
            .chart-dropdown-${uniqueId} > .dropdown.icon {
                color: ${themeColors.dropdownText} !important;
            }
        `;

        return () => {
            const el = document.getElementById(styleId);
            if (el) el.remove();
        };
    }, [themeColors, uniqueId]);

    return (
        <div style={{
            marginLeft: isSmall ? 2 : 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            flexWrap: 'nowrap', // Force side-by-side
            gap: isSmall ? '4px' : '8px'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', marginRight: isSmall ? 2 : 10 }}>
                <label style={{ cursor: 'move', marginRight: isSmall ? 2 : 5, whiteSpace: 'nowrap', fontSize: isSmall ? '10px' : 'inherit', color: 'inherit' }}>{l.analysis_type}:</label>
                <Dropdown
                    placeholder={l.select_an_option}
                    selection
                    options={options}
                    value={analysisMode}
                    onChange={handleChange}
                    className={`no-drag chart-dropdown-${uniqueId}`}
                    style={{
                        width: isSmall ? 80 : 150,
                        minWidth: isSmall ? 80 : 150,
                        padding: isSmall ? '4px' : '8px',
                        fontSize: isSmall ? '10px' : 'inherit',
                        minHeight: isSmall ? '30px' : 'inherit',
                        ...dropdownStyle
                    }}
                />
            </div>
            {analysisMode === 'dc_sweep' && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <label style={{ cursor: 'move', marginRight: isSmall ? 2 : 5, whiteSpace: 'nowrap', fontSize: isSmall ? '10px' : 'inherit', color: 'inherit' }}>Sweep:</label>
                    <Dropdown
                        placeholder='Sweep'
                        selection
                        options={dcSweepOptions}
                        value={dcSweepValue}
                        onChange={handleDcSweepChange}
                        className={`no-drag chart-dropdown-${uniqueId}`}
                        style={{
                            width: isSmall ? 75 : 150,
                            minWidth: isSmall ? 75 : 150,
                            padding: isSmall ? '4px' : '8px',
                            fontSize: isSmall ? '10px' : 'inherit',
                            minHeight: isSmall ? '30px' : 'inherit',
                            ...dropdownStyle
                        }}
                    />
                </div>
            )}
        </div>
    );
}

export default DropdownComponent;

